import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import { Box, Button, CircularProgress, Container, FormControl, FormControlLabel, Grid, MenuItem, Paper, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { get, keys, map, mean } from 'lodash';
import momentTz from 'moment-timezone';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import PACKAGE_MARKER from '../../assets/package_marker.svg';
import PACKAGE_MARKER_ORANGE from '../../assets/package_marker_orange.svg';
import PIN_MARKER from '../../assets/pin_marker.svg';
import SHIPMENT_MARKER from '../../assets/shipment_marker.svg';
import GeocodingMap from '../../components/GeocodingMap';
import { dropoffLocationsHistoryRepo } from '../../repositories';
import DeliverableAddressInfo from './DeliverableAddressInfo';
import ShipmentInfo from './ShipmentInfo';

const DetailMarker = ({ label, value }) => {
  return (
    <>
      <Grid size={4} style={{ fontWeight: '600', fontSize: 14 }}>
        {label}:
      </Grid>
      <Grid size={8} style={{ fontSize: 14, color: '#777' }}>
        {value}
      </Grid>
    </>
  );
};

const optTypes = [
  {
    value: 'shipment_id',
    label: 'Shipment ID',
  },
  {
    value: 'customer_profile_id',
    label: 'Customer Profile ID',
  },
  {
    value: 'deliverable_address_id',
    label: 'Deliverable Address ID',
  },
];

const TYPE_ID = {
  SHIPMENT_ID: 'shipment_id',
  CUSTOMER_PROFILE_ID: 'customer_profile_id',
  DELIVERABLE_ADDRESS_ID: 'deliverable_address_id',
};

function DropoffLocationsHistory() {
  const [center, setCenter] = useState({ lat: 37.7577, lng: -122.4376 });
  const [zoom, setZoom] = useState(16);
  const [type, setType] = useState(TYPE_ID.SHIPMENT_ID);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [viewBy, setViewBy] = useState(TYPE_ID.CUSTOMER_PROFILE_ID);
  const [markers, setMarkers] = useState([]);
  const [data, setData] = useState(null);
  const [typeInfo, setTypeInfo] = useState(TYPE_ID.SHIPMENT_ID);
  const [id, setId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    handleMarkerShipment(data);
  }, [viewBy]);

  const goToLandingPage = () => {
    navigate(`/`);
  };

  const handleChangeViewBy = (evt) => {
    const val = evt.target.value;
    setViewBy(val);
  };

  const handleChangeType = (evt) => {
    const val = evt.target.value;
    setType(val);
    setQuery('');
    if (val === TYPE_ID.DELIVERABLE_ADDRESS_ID) {
      setViewBy(TYPE_ID.DELIVERABLE_ADDRESS_ID);
      return;
    }
  };

  const handleSearch = async () => {
    if (!query) {
      toast('Please enter search query', {
        position: 'top-right',
        duration: 2000,
        icon: '⚠️',
      });
      return;
    }

    if ([TYPE_ID.SHIPMENT_ID].includes(type) && !/^\d+$/.test(query)) {
      toast('Please enter valid shipment ID', {
        position: 'top-right',
        duration: 2000,
        icon: '⚠️',
      });
      return;
    }

    setData(null);
    setMarkers([]);
    setSearching(true);
    const params = { [type]: query };
    const resp = await dropoffLocationsHistoryRepo.get(params);
    setSearching(false);
    if (!resp.ok) {
      toast('No data', {
        position: 'top-right',
        duration: 2000,
      });
      return;
    }
    const respData = get(resp, 'data', {});
    setData(respData);
    setZoom(19);
    setTypeInfo(type);
    setId(query);

    if ([TYPE_ID.SHIPMENT_ID, TYPE_ID.CUSTOMER_PROFILE_ID].includes(type)) {
      handleMarkerShipment(respData);
      return;
    }

    if (type === TYPE_ID.DELIVERABLE_ADDRESS_ID) {
      handleMarkerDeliverableAddress(respData);
      return;
    }
  };

  const renderContent = (info, history, headerTitle) => {
    const ts = get(history, 'ts', null);
    const lat = get(history, 'location.latitude', null);
    const lng = get(history, 'location.longitude', null);
    const type = get(history, 'type', '');

    if (type) {
      return ReactDOMServer.renderToString(
        <Grid container spacing={1} style={{ margin: 0, width: '100%' }}>
          <DetailMarker label="Type" value={headerTitle || '-'} />
          <DetailMarker label="Deliverable Address ID" value={get(history, 'deliverable_address_id', '-')} />
          {lat && lng && <DetailMarker label="At Location" value={`${lat?.toFixed(6)}, ${lng?.toFixed(6)}`} />}
          {ts && <DetailMarker label="Date Time" value={momentTz.tz(ts, momentTz.tz.guess()).format('MM/DD/YYYY HH:mm:ss z')} />}
        </Grid>,
      );
    }

    return ReactDOMServer.renderToString(
      <Grid container spacing={1} style={{ margin: 0, width: '100%' }}>
        <DetailMarker label="Type" value={headerTitle || '-'} />
        <DetailMarker label="Shipment ID" value={get(history, 'shipment_id', '-')} />
        <DetailMarker label="Customer Profile ID" value={get(history, 'customer_profile_id', '-')} />
        <DetailMarker label="Deliverable Address ID" value={get(history, 'deliverable_address_id', '-')} />
        {lat && lng && <DetailMarker label="At Location" value={`${lat?.toFixed(6)}, ${lng?.toFixed(6)}`} />}
        {ts && <DetailMarker label="Date Time" value={momentTz.tz(ts, momentTz.tz.guess()).format('MM/DD/YYYY HH:mm:ss z')} />}
      </Grid>,
    );
  };

  const renderPins = (data) => {
    const key = keys(get(data, `deliverable_address`, {}))[0];
    const deliverableAddressInfo = get(data, `deliverable_address.${key}`, {});
    const pins = get(deliverableAddressInfo, `pins`, [])?.map((p) => ({
      ...p,
      deliverable_address_id: get(deliverableAddressInfo, 'id', ''),
      location: {
        latitude: p.latitude,
        longitude: p.longitude,
      },
    }));
    return pins;
  };

  const handleMarkerShipment = (data) => {
    const newId = query || id;
    const info = get(data, `shipments.${newId}`, {});
    const histories = get(data, `location_histories`, []);
    const customerProfileID = [TYPE_ID.CUSTOMER_PROFILE_ID].includes(typeInfo) ? newId : get(info, 'customer_profile_id', '');
    const pins = renderPins(data);
    const viewByCustomerProfile = [TYPE_ID.CUSTOMER_PROFILE_ID].includes(viewBy);
    const mergedPins = histories?.concat(pins);
    const filterHistories = viewByCustomerProfile ? mergedPins?.filter((h) => h.customer_profile_id === customerProfileID) : mergedPins;

    const latitudes = map(filterHistories, 'location.latitude');
    const longitudes = map(filterHistories, 'location.longitude');
    setCenter({ lat: mean(latitudes) || 37.7577, lng: mean(longitudes) || -122.4376 });

    setMarkers(
      filterHistories?.concat(pins)?.map((h) => {
        const headerTitle = h.type ? `Pins (${h.type})` : h.shipment_id === +newId ? 'Shipment' : h.customer_profile_id === customerProfileID ? 'Customer Profile' : 'Deliverable Address';
        return {
          ...h,
          latLng: {
            lat: h.location.latitude,
            lng: h.location.longitude,
          },
          icon: h.type ? PIN_MARKER : h.shipment_id === +newId ? SHIPMENT_MARKER : h.customer_profile_id === customerProfileID ? PACKAGE_MARKER_ORANGE : PACKAGE_MARKER,
          title: h.type ? h.type : `Shipment ID: ${h.shipment_id}`,
          content: renderContent(info, h, headerTitle),
        };
      }),
    );
  };

  const handleMarkerDeliverableAddress = (data) => {
    const newId = query || id;
    const info = get(data, `deliverable_address.${newId}`, {});
    const histories = get(data, `location_histories`, [])?.filter((h) => h.deliverable_address_id === newId);
    const latitudes = map(histories, 'location.latitude');
    const longitudes = map(histories, 'location.longitude');
    setCenter({ lat: mean(latitudes) || 37.7577, lng: mean(longitudes) || -122.4376 });
    const pins = renderPins(data);

    setMarkers(
      histories?.concat(pins)?.map((h) => {
        const headerTitle = h.type ? `Pins (${h.type})` : h.shipment_id ? 'Shipment' : 'Deliverable Address';
        return {
          ...h,
          latLng: {
            lat: h.location.latitude,
            lng: h.location.longitude,
          },
          icon: h.type ? PIN_MARKER : PACKAGE_MARKER,
          title: h.type ? h.type : `Shipment ID: ${h.shipment_id}`,
          content: renderContent(info, h, headerTitle),
        };
      }),
    );
  };

  const disabledViewBy = type === TYPE_ID.DELIVERABLE_ADDRESS_ID;
  return (
    <Container maxWidth={false} style={{ height: 'calc(100% - 65px)', paddingTop: '10px' }}>
      <Paper elevation={2}>
        <Container maxWidth={false} style={{ padding: '16px' }}>
          <Grid container spacing={2}>
            <Grid size={12} style={{ marginBottom: '16px' }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  goToLandingPage();
                }}
              >
                Back to Main
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormControl variant="outlined" fullWidth size="small">
                <Select value={type} onChange={(evt) => handleChangeType(evt)}>
                  {optTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 10 }}>
              <Box display={'flex'} justifyContent={'space-between'} style={{ gap: 16 }}>
                <TextField
                  placeholder="Search by ID"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onBlur={(evt) => setQuery(evt.target.value?.trim())}
                  onKeyDown={(evt) => {
                    if (evt.key === 'Enter') {
                      handleSearch(query);
                    }
                  }}
                />
                <Box>
                  <Button variant="contained" color="primary" fullWidth disableElevation onClick={handleSearch} disabled={searching} style={{ height: '100%', width: '120px' }}>
                    {searching && <CircularProgress size={20} style={{ marginRight: '8px' }} />} Search
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid size={12}>
              <Box display="flex" alignItems={'center'} style={{ gap: '16px' }}>
                <Box fontWeight={600}>View dropoff location by</Box>
                <RadioGroup row value={viewBy} onChange={(evt) => handleChangeViewBy(evt)}>
                  <FormControlLabel value={TYPE_ID.CUSTOMER_PROFILE_ID} control={<Radio />} label="Customer Profile" disabled={disabledViewBy} />
                  <FormControlLabel value={TYPE_ID.DELIVERABLE_ADDRESS_ID} control={<Radio />} label="Deliverable Address" disabled={disabledViewBy} />
                </RadioGroup>
              </Box>
            </Grid>

            <Grid size={12}>
              <Grid container spacing={2} style={{ minHeight: '420px', height: 'calc(100vh - 290px)' }}>
                <Grid size={2} style={{ overflow: 'auto', border: '1px solid #ccc', borderRadius: '4px', height: '100%' }} p={1}>
                  <Box fontWeight={600} mb={2} textAlign={'center'}>
                    View Info
                  </Box>
                  {typeInfo === TYPE_ID.SHIPMENT_ID && <ShipmentInfo data={data} id={+id} />}
                  {[TYPE_ID.CUSTOMER_PROFILE_ID, TYPE_ID.DELIVERABLE_ADDRESS_ID].includes(typeInfo) && <DeliverableAddressInfo data={data} id={id} />}
                </Grid>
                <Grid size={10} style={{ padding: '0 8px' }}>
                  <GeocodingMap center={center} initialZoom={zoom} google_key={`${import.meta.env.REACT_APP_GOOGLE_KEY}`} markers={markers} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Paper>
    </Container>
  );
}

export default DropoffLocationsHistory;
